import { parseColor } from "./color";
import { cascade, CssRule, Declarations, parseDeclarations, parseStylesheet } from "./css";
import {
  apply,
  decompose,
  IDENTITY,
  Matrix,
  meanScale,
  multiply,
  parseNumberList,
  parseTransform,
  scale,
  translate,
} from "./matrix";
import { ellipsePath, parsePath, polyPath, rectPath, Segment, transformPath } from "./path";
import { ImportNode, ImportStyle, Paint, ParsedSvg, TextAlign } from "./types";
import { isXmlElement, parseXml, SvgImportError, XmlElement, XmlNode } from "./xml";

export const SVG_IMPORT_LIMITS = {
  maxBytes: 10 * 1024 * 1024,
  maxElements: 20000,
  maxDepth: 200,
  maxUseDepth: 8,
  maxPathSegments: 300000,
  maxImageBytes: 8 * 1024 * 1024,
} as const;

const DEFAULT_WIDTH = 300;
const DEFAULT_HEIGHT = 150;
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_FONT = "Inter";
// Fraction of the font size between the line-box top and the baseline (customText uses lineHeight 1).
const BASELINE_RATIO = 0.85;
// Rough average glyph advance as a fraction of the font size, used only to place anchored text.
const GLYPH_WIDTH_RATIO = 0.55;

const UNIT_TO_PX: Readonly<Record<string, number>> = {
  "": 1,
  px: 1,
  pt: 96 / 72,
  pc: 16,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  in: 96,
};

const SKIPPED_SILENTLY = new Set([
  "defs",
  "title",
  "desc",
  "metadata",
  "style",
  "linearGradient",
  "radialGradient",
  "symbol",
  "marker",
  "clipPath",
  "mask",
  "pattern",
  "filter",
  "font",
  "font-face",
]);
const CONTAINERS = new Set(["g", "a", "switch"]);
const SAFE_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp);base64,([a-z0-9+/=\s]+)$/i;
const SAFE_FONT_RE = /^[\w -]+$/;

const INHERITED = [
  "fill",
  "stroke",
  "stroke-width",
  "fill-opacity",
  "stroke-opacity",
  "fill-rule",
  "stroke-linecap",
  "stroke-linejoin",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "text-anchor",
  "color",
  "letter-spacing",
  "visibility",
] as const;

const PRESENTATION = new Set<string>([
  ...INHERITED,
  "opacity",
  "display",
  "clip-path",
  "mask",
  "filter",
  "stop-color",
  "stop-opacity",
]);

type Computed = Readonly<Record<string, string>>;

interface Context {
  readonly ctm: Matrix;
  readonly style: Computed;
  readonly opacity: number;
  readonly useDepth: number;
  readonly viewport: { readonly w: number; readonly h: number };
}

class Walker {
  private readonly rules: CssRule[];
  private readonly ids = new Map<string, XmlElement>();
  private readonly warnings = new Set<string>();
  private budget = SVG_IMPORT_LIMITS.maxElements;
  private segmentBudget = SVG_IMPORT_LIMITS.maxPathSegments;
  private removedHandlers = 0;

  constructor(root: XmlElement) {
    const styles: string[] = [];
    this.index(root, styles);
    this.rules = parseStylesheet(styles.join("\n"));
    if (this.removedHandlers > 0) this.warn("Scripts and event handlers were removed");
  }

  getWarnings(): string[] {
    return Array.from(this.warnings);
  }

  warn(message: string): void {
    this.warnings.add(message);
  }

  private index(el: XmlElement, styles: string[]): void {
    if (el.attrs.id && !this.ids.has(el.attrs.id)) this.ids.set(el.attrs.id, el);
    this.removedHandlers += Object.keys(el.attrs).filter((name) => /^on/i.test(name)).length;
    if (el.name === "script") this.removedHandlers += 1;
    if (el.name === "style") styles.push(textContent(el));
    el.children.forEach((child) => isXmlElement(child) && this.index(child, styles));
  }

  computeStyle(el: XmlElement, parent: Computed): { style: Computed; own: Declarations } {
    const own: Declarations = {};
    Object.entries(el.attrs).forEach(([key, value]) => {
      if (PRESENTATION.has(key)) own[key] = value.trim();
    });
    Object.assign(own, cascade(this.rules, el.name, el.attrs));
    if (el.attrs.style) Object.assign(own, parseDeclarations(el.attrs.style));
    if (Object.values(own).some((v) => /url\(\s*['"]?(?!#)/i.test(v))) this.warn("External references were removed");

    const style: Record<string, string> = {};
    INHERITED.forEach((key) => {
      const value = own[key];
      if (value !== undefined && value !== "inherit") style[key] = value;
      else if (parent[key] !== undefined) style[key] = parent[key];
    });
    ["opacity", "display", "clip-path", "mask", "filter"].forEach((key) => {
      if (own[key] !== undefined) style[key] = own[key];
    });
    return { style, own };
  }

  consume(): void {
    this.budget -= 1;
    if (this.budget < 0) {
      throw new SvgImportError(`SVG is too complex (more than ${SVG_IMPORT_LIMITS.maxElements} elements)`);
    }
  }

  consumeSegments(count: number): void {
    this.segmentBudget -= count;
    if (this.segmentBudget < 0) throw new SvgImportError("SVG paths are too complex to import");
  }

  lookup(href: string | undefined): XmlElement | undefined {
    if (!href || !href.startsWith("#")) return undefined;
    return this.ids.get(href.slice(1));
  }

  resolvePaint(value: string | undefined, style: Computed, opacityKey: string): Paint | null {
    if (!value || value === "none") return null;
    const ref = /^url\(\s*['"]?#([^)'"]+)['"]?\s*\)\s*(.*)$/.exec(value);
    if (ref) {
      const target = this.ids.get(ref[1]);
      const stop = target ? this.firstStop(target, 0) : null;
      if (target?.name === "pattern") this.warn("Pattern fills were replaced with a solid colour");
      else if (stop) this.warn("Gradients were simplified to a solid colour");
      const colour = stop ?? parseColor(ref[2] || undefined);
      return colour ? { color: colour.hex, opacity: colour.alpha * opacityOf(style[opacityKey]) } : null;
    }
    if (/^url\(/i.test(value)) return null;
    const raw = value.toLowerCase() === "currentcolor" ? style.color ?? "#000000" : value;
    const colour = parseColor(raw);
    return colour ? { color: colour.hex, opacity: colour.alpha * opacityOf(style[opacityKey]) } : null;
  }

  private firstStop(el: XmlElement, depth: number): { hex: string; alpha: number } | null {
    if (depth > 4) return null;
    const stop = el.children.find((c): c is XmlElement => isXmlElement(c) && c.name === "stop");
    if (stop) {
      const own = { ...stop.attrs, ...(stop.attrs.style ? parseDeclarations(stop.attrs.style) : {}) };
      const colour = parseColor(own["stop-color"] ?? "black");
      return colour ? { hex: colour.hex, alpha: colour.alpha * opacityOf(own["stop-opacity"]) } : null;
    }
    const linked = this.lookup(el.attrs.href ?? el.attrs["xlink:href"]);
    return linked ? this.firstStop(linked, depth + 1) : null;
  }
}

const opacityOf = (value: string | undefined): number => {
  if (value === undefined) return 1;
  const n = value.trim().endsWith("%") ? parseFloat(value) / 100 : parseFloat(value);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
};

const textContent = (el: XmlElement): string =>
  el.children.map((c) => (isXmlElement(c) ? textContent(c) : c.text)).join("");

export const parseLength = (value: string | undefined, reference: number, fontSize = DEFAULT_FONT_SIZE): number => {
  if (value === undefined) return NaN;
  const match = /^\s*([-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?)\s*(px|pt|pc|mm|cm|in|em|ex|%)?\s*$/i.exec(value);
  if (!match) return NaN;
  const n = parseFloat(match[1]);
  const unit = (match[2] ?? "").toLowerCase();
  if (unit === "%") return (n / 100) * reference;
  if (unit === "em") return n * fontSize;
  if (unit === "ex") return n * fontSize * 0.5;
  return n * (UNIT_TO_PX[unit] ?? 1);
};

const num = (value: string | undefined, reference: number, fallback = 0): number => {
  const n = parseLength(value, reference);
  return Number.isFinite(n) ? n : fallback;
};

/** Maps a viewBox onto a viewport per preserveAspectRatio (default xMidYMid meet). */
export const viewBoxTransform = (
  viewBox: readonly number[],
  width: number,
  height: number,
  preserve = "xMidYMid meet"
): Matrix => {
  const [vx, vy, vw, vh] = viewBox;
  if (!(vw > 0 && vh > 0)) return IDENTITY;
  const sx = width / vw;
  const sy = height / vh;
  const [align = "xMidYMid", mode = "meet"] = preserve.trim().split(/\s+/);
  if (align === "none") return multiply(scale(sx, sy), translate(-vx, -vy));
  const s = mode === "slice" ? Math.max(sx, sy) : Math.min(sx, sy);
  const free = { x: width - vw * s, y: height - vh * s };
  const factor = (axis: "x" | "y"): number => {
    const part = axis === "x" ? align.slice(0, 4) : align.slice(4);
    if (/Mid/i.test(part)) return 0.5;
    return /Max/i.test(part) ? 1 : 0;
  };
  return multiply(translate(free.x * factor("x"), free.y * factor("y")), multiply(scale(s), translate(-vx, -vy)));
};

const fontWeightOf = (value: string | undefined): number => {
  if (!value) return 400;
  if (value === "bold" || value === "bolder") return 700;
  if (value === "lighter") return 300;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? Math.min(900, Math.max(100, Math.round(n / 100) * 100)) : 400;
};

const fontFamilyOf = (value: string | undefined): string => {
  const first = (value ?? "")
    .split(",")[0]
    ?.trim()
    .replace(/^['"]|['"]$/g, "");
  if (!first || /^(sans-serif|serif|monospace|system-ui|cursive|fantasy)$/i.test(first)) return DEFAULT_FONT;
  return SAFE_FONT_RE.test(first) ? first : DEFAULT_FONT;
};

const alignOf = (anchor: string | undefined): TextAlign =>
  anchor === "middle" ? "middle" : anchor === "end" ? "end" : "start";

const styleFor = (walker: Walker, ctx: Context, style: Computed, opacity: number): ImportStyle => {
  const sw = num(style["stroke-width"], Math.hypot(ctx.viewport.w, ctx.viewport.h) / Math.SQRT2, 1);
  const stroke = walker.resolvePaint(style.stroke, style, "stroke-opacity");
  return {
    fill: walker.resolvePaint(style.fill ?? "black", style, "fill-opacity"),
    stroke: stroke && sw > 0 ? stroke : null,
    strokeWidth: sw * meanScale(ctx.ctm),
    opacity,
    fillRule: style["fill-rule"] === "evenodd" ? "evenodd" : "nonzero",
    lineCap: style["stroke-linecap"],
    lineJoin: style["stroke-linejoin"],
  };
};

interface Lines {
  lines: string[];
  baselines: number[];
  xs: number[];
  style: Computed | null;
}

const collectText = (walker: Walker, el: XmlElement, parentStyle: Computed, acc: Lines, ctx: Context): void => {
  let cursorY = acc.baselines.length ? acc.baselines[acc.baselines.length - 1] : 0;
  el.children.forEach((child: XmlNode) => {
    if (!isXmlElement(child)) {
      const text = child.text.replace(/\s+/g, " ");
      if (!text.trim() && acc.lines.length === 0) return;
      if (acc.lines.length === 0) {
        acc.lines.push("");
        acc.baselines.push(cursorY);
        acc.xs.push(0);
      }
      acc.lines[acc.lines.length - 1] += text;
      if (!acc.style && text.trim()) acc.style = parentStyle;
      return;
    }
    if (child.name !== "tspan" && child.name !== "textPath" && child.name !== "a") return;
    walker.consume();
    const { style } = walker.computeStyle(child, parentStyle);
    if (style.display === "none") return;
    const fontSize = num(style["font-size"], ctx.viewport.h, DEFAULT_FONT_SIZE);
    const y = parseNumberList(child.attrs.y ?? "")[0];
    const dy = parseLength(child.attrs.dy?.split(/[\s,]+/)[0], ctx.viewport.h, fontSize);
    const nextY = Number.isFinite(y) ? y : cursorY + (Number.isFinite(dy) ? dy : 0);
    if (acc.lines.length === 0 || Math.abs(nextY - cursorY) > 0.01) {
      const x = parseNumberList(child.attrs.x ?? "")[0];
      acc.lines.push("");
      acc.baselines.push(nextY);
      acc.xs.push(Number.isFinite(x) ? x : acc.xs[acc.xs.length - 1] ?? 0);
    }
    cursorY = nextY;
    collectText(walker, child, style, acc, ctx);
  });
};

const buildText = (walker: Walker, el: XmlElement, style: Computed, ctx: Context): ImportNode | null => {
  const x = parseNumberList(el.attrs.x ?? "0")[0] ?? 0;
  const y = parseNumberList(el.attrs.y ?? "0")[0] ?? 0;
  const acc: Lines = { lines: [""], baselines: [y], xs: [x], style: null };
  const ownText = el.children.some((c) => !isXmlElement(c) && c.text.trim());
  collectText(walker, el, style, acc, ctx);
  // Drop the seed line when the text lives entirely in positioned tspans.
  if (!ownText && acc.lines.length > 1 && !acc.lines[0].trim()) {
    acc.lines.shift();
    acc.baselines.shift();
    acc.xs.shift();
  }
  const firstX = acc.xs[0];
  const lines = acc.lines.map((l) => l.trim());
  if (!lines.some(Boolean)) return null;

  const effective = acc.style ?? style;
  const localSize = num(effective["font-size"], ctx.viewport.h, DEFAULT_FONT_SIZE);
  const s = meanScale(ctx.ctm);
  const fontSize = localSize * s;
  const fill = walker.resolvePaint(effective.fill ?? "black", effective, "fill-opacity");
  const align = alignOf(effective["text-anchor"]);
  const longest = Math.max(...lines.map((l) => l.length));
  const estimatedWidth = longest * localSize * GLYPH_WIDTH_RATIO;
  const anchorShift = align === "middle" ? estimatedWidth / 2 : align === "end" ? estimatedWidth : 0;
  const firstBaseline = acc.baselines[0];
  const localTopLeft = { x: firstX - anchorShift, y: firstBaseline - localSize * BASELINE_RATIO };
  const origin = apply(ctx.ctm, localTopLeft);
  const parts = decompose(ctx.ctm);
  if (!parts) walker.warn("Skewed or mirrored text was straightened");
  const spacing = acc.baselines.length > 1 ? (acc.baselines[1] - acc.baselines[0]) / localSize : 1;

  return {
    kind: "text",
    x: origin.x,
    y: origin.y,
    rotation: parts ? parts.rotation : Math.atan2(ctx.ctm.b, ctx.ctm.a),
    anchor: apply(ctx.ctm, { x: firstX, y: firstBaseline }),
    lines,
    fontSize,
    fontFamily: fontFamilyOf(effective["font-family"]),
    fontWeight: fontWeightOf(effective["font-weight"]),
    italic: /italic|oblique/i.test(effective["font-style"] ?? ""),
    align,
    color: fill?.color ?? "#000000",
    opacity: ctx.opacity * (fill?.opacity ?? 1),
    lineHeight: Number.isFinite(spacing) && spacing > 0.5 && spacing < 4 ? spacing : 1,
    letterSpacing: num(effective["letter-spacing"], ctx.viewport.w, 0) * s,
  };
};

const buildImage = (walker: Walker, el: XmlElement, ctx: Context): ImportNode | null => {
  const href = (el.attrs.href ?? el.attrs["xlink:href"] ?? "").trim();
  const match = SAFE_IMAGE_RE.exec(href);
  if (!match) {
    walker.warn(
      /^data:image\/svg/i.test(href)
        ? "Embedded SVG images were skipped"
        : "Linked (non-embedded) images were skipped for security"
    );
    return null;
  }
  if (href.length > SVG_IMPORT_LIMITS.maxImageBytes) {
    walker.warn("An embedded image was too large and was skipped");
    return null;
  }
  const w = num(el.attrs.width, ctx.viewport.w);
  const h = num(el.attrs.height, ctx.viewport.h);
  if (!(w > 0 && h > 0)) return null;
  const box = placeBox(walker, ctx.ctm, num(el.attrs.x, ctx.viewport.w), num(el.attrs.y, ctx.viewport.h), w, h);
  const mime = match[1].toLowerCase() === "jpg" ? "jpeg" : match[1].toLowerCase();
  return {
    kind: "image",
    ...box,
    href: `data:image/${mime};base64,${match[2].replace(/\s+/g, "")}`,
    mimeType: `image/${mime}`,
    opacity: ctx.opacity,
  };
};

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

// Places an axis-aligned local box in root space; skew/mirror fall back to the transformed bounds.
const placeBox = (walker: Walker, ctm: Matrix, x: number, y: number, w: number, h: number): Box => {
  const parts = decompose(ctm);
  if (parts) {
    const origin = apply(ctm, { x, y });
    return { x: origin.x, y: origin.y, w: w * parts.scaleX, h: h * parts.scaleY, rotation: parts.rotation };
  }
  walker.warn("Skewed or mirrored images were straightened");
  const corners = [apply(ctm, { x, y }), apply(ctm, { x: x + w, y }), apply(ctm, { x, y: y + h })];
  corners.push(apply(ctm, { x: x + w, y: y + h }));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY, rotation: 0 };
};

const pathNode = (walker: Walker, local: Segment[], ctx: Context, style: ImportStyle): ImportNode | null => {
  if (local.length < 2) return null;
  walker.consumeSegments(local.length);
  return { kind: "path", segments: transformPath(local, ctx.ctm), style };
};

const buildShape = (walker: Walker, el: XmlElement, ctx: Context, style: ImportStyle): ImportNode | null => {
  const { w: vw, h: vh } = ctx.viewport;
  const a = el.attrs;
  switch (el.name) {
    case "rect": {
      const w = num(a.width, vw);
      const h = num(a.height, vh);
      if (!(w > 0 && h > 0)) return null;
      const x = num(a.x, vw);
      const y = num(a.y, vh);
      const rx = Math.min(num(a.rx, vw, num(a.ry, vh)), w / 2, h / 2);
      const parts = decompose(ctx.ctm);
      if (!parts) return pathNode(walker, rectPath(x, y, w, h, rx), ctx, style);
      const origin = apply(ctx.ctm, { x, y });
      return {
        kind: "rect",
        x: origin.x,
        y: origin.y,
        w: w * parts.scaleX,
        h: h * parts.scaleY,
        rotation: parts.rotation,
        radius: rx * Math.min(parts.scaleX, parts.scaleY),
        style,
      };
    }
    case "circle":
    case "ellipse": {
      const rx = el.name === "circle" ? num(a.r, Math.hypot(vw, vh) / Math.SQRT2) : num(a.rx, vw, num(a.ry, vh));
      const ry = el.name === "circle" ? rx : num(a.ry, vh, rx);
      if (!(rx > 0 && ry > 0)) return null;
      const cx = num(a.cx, vw);
      const cy = num(a.cy, vh);
      const parts = decompose(ctx.ctm);
      if (!parts) return pathNode(walker, ellipsePath(cx, cy, rx, ry), ctx, style);
      const origin = apply(ctx.ctm, { x: cx - rx, y: cy - ry });
      return {
        kind: "ellipse",
        x: origin.x,
        y: origin.y,
        w: 2 * rx * parts.scaleX,
        h: 2 * ry * parts.scaleY,
        rotation: parts.rotation,
        style,
      };
    }
    case "line":
      return pathNode(walker, polyPath([num(a.x1, vw), num(a.y1, vh), num(a.x2, vw), num(a.y2, vh)], false), ctx, {
        ...style,
        fill: null,
      });
    case "polyline":
    case "polygon":
      return pathNode(walker, polyPath(parseNumberList(a.points ?? ""), el.name === "polygon"), ctx, style);
    case "path":
      return pathNode(walker, parsePath(a.d ?? ""), ctx, style);
    default:
      return null;
  }
};

const nestedViewportMatrix = (el: XmlElement, ctx: Context): { m: Matrix; w: number; h: number } => {
  const w = num(el.attrs.width, ctx.viewport.w, ctx.viewport.w);
  const h = num(el.attrs.height, ctx.viewport.h, ctx.viewport.h);
  const offset = translate(num(el.attrs.x, ctx.viewport.w), num(el.attrs.y, ctx.viewport.h));
  const viewBox = parseNumberList(el.attrs.viewBox ?? "");
  if (viewBox.length !== 4) return { m: offset, w, h };
  return {
    m: multiply(offset, viewBoxTransform(viewBox, w, h, el.attrs.preserveAspectRatio)),
    w: viewBox[2],
    h: viewBox[3],
  };
};

const walk = (walker: Walker, el: XmlElement, parent: Context): ImportNode[] => {
  if (SKIPPED_SILENTLY.has(el.name)) return [];
  if (el.name === "script") return [];
  if (el.name === "foreignObject") {
    walker.warn("Embedded HTML (foreignObject) is not supported and was skipped");
    return [];
  }
  walker.consume();

  const { style } = walker.computeStyle(el, parent.style);
  if (style.display === "none" || style.visibility === "hidden") return [];
  if (style["clip-path"] && style["clip-path"] !== "none") walker.warn("Clipping paths were ignored");
  if (style.mask && style.mask !== "none") walker.warn("Masks were ignored");
  if (style.filter && style.filter !== "none") walker.warn("Filters (blur, shadows) were ignored");

  const opacity = parent.opacity * opacityOf(style.opacity);
  let ctm = multiply(parent.ctm, parseTransform(el.attrs.transform));
  let viewport = parent.viewport;

  if (el.name === "use") {
    const target = walker.lookup(el.attrs.href ?? el.attrs["xlink:href"]);
    if (!target || parent.useDepth >= SVG_IMPORT_LIMITS.maxUseDepth) return [];
    ctm = multiply(ctm, translate(num(el.attrs.x, viewport.w), num(el.attrs.y, viewport.h)));
    const ctx: Context = { ctm, style, opacity, useDepth: parent.useDepth + 1, viewport };
    const children = target.name === "symbol" ? walkContainer(walker, target, ctx, true) : walk(walker, target, ctx);
    return wrapGroup(children);
  }

  if (el.name === "svg") {
    const nested = nestedViewportMatrix(el, parent);
    ctm = multiply(ctm, nested.m);
    viewport = { w: nested.w, h: nested.h };
  }

  const ctx: Context = { ctm, style, opacity, useDepth: parent.useDepth, viewport };
  if (CONTAINERS.has(el.name) || el.name === "svg") return wrapGroup(walkChildren(walker, el, ctx));
  if (el.name === "text") {
    const text = buildText(walker, el, style, ctx);
    return text ? [text] : [];
  }
  if (el.name === "image") {
    const image = buildImage(walker, el, ctx);
    return image ? [image] : [];
  }
  const shape = buildShape(walker, el, ctx, styleFor(walker, ctx, style, opacity));
  if (!shape && !["rect", "circle", "ellipse", "line", "polyline", "polygon", "path"].includes(el.name)) {
    walker.warn(`Unsupported element <${el.name}> was skipped`);
  }
  return shape ? [shape] : [];
};

const walkChildren = (walker: Walker, el: XmlElement, ctx: Context): ImportNode[] =>
  el.children.flatMap((child) => (isXmlElement(child) ? walk(walker, child, ctx) : []));

// A <symbol> used via <use> is a nested viewport, like an inner <svg>.
const walkContainer = (walker: Walker, symbol: XmlElement, ctx: Context, isSymbol: boolean): ImportNode[] => {
  if (!isSymbol) return walkChildren(walker, symbol, ctx);
  const nested = nestedViewportMatrix(symbol, ctx);
  const inner: Context = { ...ctx, ctm: multiply(ctx.ctm, nested.m), viewport: { w: nested.w, h: nested.h } };
  return walkChildren(walker, symbol, inner);
};

// Groups of one are flattened so the canvas isn't full of pointless single-child groups.
const wrapGroup = (children: ImportNode[]): ImportNode[] =>
  children.length > 1 ? [{ kind: "group", children }] : children;

const rootSize = (root: XmlElement): { width: number; height: number; viewBox: number[] } => {
  const viewBox = parseNumberList(root.attrs.viewBox ?? "");
  const hasViewBox = viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0;
  const read = (value: string | undefined, fromViewBox: number, fallback: number): number => {
    if (value && !value.trim().endsWith("%")) {
      const n = parseLength(value, 0);
      if (n > 0) return n;
    }
    return hasViewBox ? fromViewBox : fallback;
  };
  let width = read(root.attrs.width, viewBox[2], DEFAULT_WIDTH);
  let height = read(root.attrs.height, viewBox[3], DEFAULT_HEIGHT);
  // Only one dimension given: keep the viewBox aspect ratio.
  if (hasViewBox && root.attrs.width && !root.attrs.height) height = (width * viewBox[3]) / viewBox[2];
  if (hasViewBox && root.attrs.height && !root.attrs.width) width = (height * viewBox[2]) / viewBox[3];
  return { width, height, viewBox: hasViewBox ? viewBox : [] };
};

/** Parses untrusted SVG text into normalised, renderer-agnostic nodes. Throws SvgImportError. */
export const parseSvg = (source: string): ParsedSvg => {
  if (typeof source !== "string" || source.trim().length === 0) throw new SvgImportError("The SVG file is empty");
  if (source.length > SVG_IMPORT_LIMITS.maxBytes) {
    throw new SvgImportError(`SVG file is too large (max ${SVG_IMPORT_LIMITS.maxBytes / 1024 / 1024} MB)`);
  }
  const root = parseXml(source, { maxElements: SVG_IMPORT_LIMITS.maxElements, maxDepth: SVG_IMPORT_LIMITS.maxDepth });
  const { width, height, viewBox } = rootSize(root);
  const walker = new Walker(root);
  const rootCtm = viewBox.length ? viewBoxTransform(viewBox, width, height, root.attrs.preserveAspectRatio) : IDENTITY;
  const viewport = viewBox.length ? { w: viewBox[2], h: viewBox[3] } : { w: width, h: height };
  const { style } = walker.computeStyle(root, {});
  const ctx: Context = { ctm: rootCtm, style, opacity: opacityOf(style.opacity), useDepth: 0, viewport };
  const nodes = walkChildren(walker, root, ctx);
  return { width, height, nodes, warnings: walker.getWarnings() };
};
